import React, { useEffect, useState, Component } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getMisl, setMisl, clearMisl, type StoredMislMember } from './mislStore';
import { ChevronLeft, ArrowRight, MapPin, Plus, Copy, Check, MessageCircle, Share2, UserPlus, Crown, User, Sparkles, Trophy, Flame, Swords, Zap, PartyPopper } from 'lucide-react';
const CornerOrnaments = () => <>
    <span className="absolute top-0 left-0 w-2.5 h-2.5 border-t border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-1850" />
    <span className="absolute top-0 right-0 w-2.5 h-2.5 border-t border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-1851" />
    <span className="absolute bottom-0 left-0 w-2.5 h-2.5 border-b border-l border-[#D4AF37]/50 pointer-events-none" data-id="element-1852" />
    <span className="absolute bottom-0 right-0 w-2.5 h-2.5 border-b border-r border-[#D4AF37]/50 pointer-events-none" data-id="element-1853" />
  </>;
type Member = {
  id: string;
  name: string;
  handle: string;
  role: 'founder' | 'member';
  joinedAt: string;
};
export const CreateMislPanel = ({
  embedded = true,
  onClose
}: {
  embedded?: boolean;
  onClose?: () => void;
}) => {
  const [created, setCreated] = useState(false);
  const [justCreated, setJustCreated] = useState(false);
  const [copied, setCopied] = useState(false);
  const [name, setName] = useState('');
  const [region, setRegion] = useState('');
  const [members, setMembers] = useState<Member[]>([]);
  const [simulating, setSimulating] = useState(false);
  const target = 5;
  const memberCount = members.length;
  useEffect(() => {
    const existing = getMisl();
    if (existing) {
      setName(existing.name);
      setRegion(existing.region);
      setMembers(existing.members as Member[]);
      setCreated(true);
    }
  }, []);
  useEffect(() => {
    if (!created) return;
    setMisl({
      name,
      region,
      members: members as StoredMislMember[],
      target,
      createdAt: Date.now()
    });
  }, [created, name, region, members]);
  const progress = Math.min(100, Math.round(memberCount / target * 100));
  const isActive = memberCount >= target;
  const inviteLink = `lahoretreasury.com/misl/${(name || 'your-misl').toLowerCase().replace(/\s+/g, '-')}`;
  const handleCreate = () => {
    if (!name.trim()) return;
    setMembers([{
      id: 'me',
      name: 'Harnoor S.',
      handle: '@harnoor_s',
      role: 'founder',
      joinedAt: 'just now'
    }]);
    setCreated(true);
    setJustCreated(true);
  };
  useEffect(() => {
    if (justCreated) {
      const t = setTimeout(() => setJustCreated(false), 2200);
      return () => clearTimeout(t);
    }
  }, [justCreated]);
  const handleCopy = () => {
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };
  const handleWhatsApp = () => {
    const msg = encodeURIComponent(`🛡️ Join my Misl on Lahore Treasury — ${name || 'a new house'}.\nWe need ${target - memberCount} more to activate our house in the Court.\n\n${inviteLink}`);
    window.open(`https://wa.me/?text=${msg}`, '_blank');
  };
  const handleSimulateJoin = () => {
    const samplePeople = [{
      name: 'Arjan S.',
      handle: '@arjan_s'
    }, {
      name: 'Simran K.',
      handle: '@simran_k'
    }, {
      name: 'Harjeet K.',
      handle: '@harjeet_k'
    }, {
      name: 'Dilraj S.',
      handle: '@dilraj_s'
    }];
    const next = samplePeople[memberCount - 1];
    if (!next) return;
    setSimulating(true);
    setTimeout(() => {
      setMembers(prev => [...prev, {
        id: `m${prev.length}`,
        name: next.name,
        handle: next.handle,
        role: 'member',
        joinedAt: 'just now'
      }]);
      setSimulating(false);
    }, 400);
  };
  // ============ Stage 1: Pre-creation ============
  const steps = [{
    n: '01',
    icon: Crown,
    title: 'Name Your House',
    body: 'Pick a name your clan will rally around — your Misl is yours to lead.'
  }, {
    n: '02',
    icon: Share2,
    title: 'Invite Your People',
    body: 'Send your private invite link on WhatsApp. Everyone you bring in can invite more.'
  }, {
    n: '03',
    icon: Flame,
    title: 'Activate at 5 Members',
    body: 'Reach 5 members and your house goes live — earning XP, ranking, and Court standing together.'
  }];
  const perks = [{
    icon: Trophy,
    label: 'Misl Leaderboard'
  }, {
    icon: Swords,
    label: 'Group Rewards'
  }, {
    icon: Zap,
    label: 'Founder Crown'
  }, {
    icon: Sparkles,
    label: 'Custom Crest'
  }];
  return <div className={embedded ? '' : 'max-w-3xl mx-auto px-4 py-16'} data-id="element-1854">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-[#D4AF37]/20 pb-4 mb-8" data-id="element-1855">
        <div data-id="element-1856">
          <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.35em] uppercase mb-1.5" data-id="element-1857">
            {created ? 'Your Misl' : 'Found Your House'}
          </p>
          <h3 className="font-serif text-2xl sm:text-3xl text-[#FDFBF7] leading-tight" data-id="element-1858">
            {created ? name : 'Create Your Own Misl'}
          </h3>
        </div>
        {embedded && onClose && <button onClick={onClose} className="flex items-center gap-2 text-[#D4AF37] hover:text-[#E5C158] text-[10px] uppercase tracking-widest border border-[#D4AF37]/30 hover:bg-[#D4AF37]/10 px-3 py-2 transition-colors" data-id="element-1859">
            <ChevronLeft className="w-3 h-3" data-id="element-1860" />
            Back
          </button>}
      </div>

      {!created /* ============ STAGE 1: EXCITING + INSTRUCTIVE CREATE ============ */ ? <div className="space-y-8" data-id="element-1861">
          {/* Inspiring banner */}
          <motion.div initial={{
        opacity: 0,
        y: 12
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.5
      }} className="relative border border-[#D4AF37]/30 bg-gradient-to-br from-[#1A1410] via-[#131B2B] to-[#0B0F19] p-6 sm:p-7 overflow-hidden" data-id="element-1862">
            <CornerOrnaments data-id="element-1863" />
            <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-1864" />
            <div className="absolute -right-16 -top-16 w-56 h-56 bg-[#D4AF37]/10 blur-[100px] rounded-full pointer-events-none" data-id="element-1865" />
            <div className="absolute -left-16 -bottom-16 w-56 h-56 bg-[#8B0000]/10 blur-[100px] rounded-full pointer-events-none" data-id="element-1866" />

            <div className="relative z-10 flex items-start gap-5" data-id="element-1867">
              <motion.div animate={{
            rotate: [0, -4, 4, -4, 0]
          }} transition={{
            duration: 4,
            repeat: Infinity,
            repeatDelay: 2
          }} className="shrink-0 w-14 h-14 border-2 border-[#D4AF37]/60 bg-[#D4AF37]/10 flex items-center justify-center" data-id="element-1868">
                <Crown className="w-7 h-7 text-[#D4AF37]" strokeWidth={1.5} data-id="element-1869" />
              </motion.div>
              <div className="flex-1" data-id="element-1870">
                <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase font-bold mb-2 flex items-center gap-2" data-id="element-1871">
                  <Sparkles className="w-3 h-3" data-id="element-1872" />
                  Forge Your Legacy
                </p>
                <h4 className="font-serif text-xl sm:text-2xl text-[#FDFBF7] leading-tight mb-2" data-id="element-1873">
                  Lead a Clan in the Lahore Court
                </h4>
                <p className="text-[#FDFBF7]/60 text-sm leading-relaxed" data-id="element-1874">
                  A Misl is your house — your name, your people, your standing.
                  Found yours, gather your sangat, and rise together.
                </p>
              </div>
            </div>

            {/* Perks chips */}
            <div className="relative z-10 mt-5 flex flex-wrap gap-2" data-id="element-1875">
              {perks.map(p => <span key={p.label} className="inline-flex items-center gap-1.5 px-2.5 py-1 border border-[#D4AF37]/25 bg-[#0B0F19]/60 text-[#FDFBF7]/70 text-[10px] tracking-[0.2em] uppercase" data-id="element-1876">
                  <p.icon className="w-3 h-3 text-[#D4AF37]" data-id="element-1877" />
                  {p.label}
                </span>)}
            </div>
          </motion.div>

          {/* Visible 3-step instructions */}
          <div data-id="element-1878">
            <div className="flex items-center gap-3 mb-4" data-id="element-1879">
              <span className="font-mono text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase" data-id="element-1880">
                How It Works
              </span>
              <span className="flex-1 h-px bg-[#D4AF37]/15" data-id="element-1881" />
              <span className="text-[#FDFBF7]/40 text-[10px] tracking-[0.25em] uppercase" data-id="element-1882">
                3 Steps
              </span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3" data-id="element-1883">
              {steps.map((s, i) => <motion.div key={s.n} initial={{
            opacity: 0,
            y: 14
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4,
            delay: 0.1 + i * 0.1
          }} className="relative border border-[#D4AF37]/15 bg-[#0B0F19] p-4 hover:border-[#D4AF37]/40 transition-colors" data-id="element-1884">
                  <div className="flex items-center justify-between mb-3" data-id="element-1885">
                    <div className="w-9 h-9 border border-[#D4AF37]/40 bg-[#131B2B] flex items-center justify-center" data-id="element-1886">
                      <s.icon className="w-4 h-4 text-[#D4AF37]" data-id="element-1887" />
                    </div>
                    <span className="font-mono text-[#D4AF37]/40 text-[10px] tracking-[0.25em]" data-id="element-1888">
                      {s.n}
                    </span>
                  </div>
                  <h5 className="font-serif text-sm text-[#FDFBF7] leading-tight mb-1.5" data-id="element-1889">
                    {s.title}
                  </h5>
                  <p className="text-[#FDFBF7]/50 text-[11px] leading-relaxed" data-id="element-1890">
                    {s.body}
                  </p>
                </motion.div>)}
            </div>
          </div>

          {/* Create form */}
          <motion.div initial={{
        opacity: 0,
        y: 14
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4,
        delay: 0.4
      }} className="border border-[#D4AF37]/25 bg-[#0B0F19] p-6 sm:p-7 relative" data-id="element-1891">
            <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37]/60 to-transparent" data-id="element-1892" />
            <p className="text-[#D4AF37] text-[10px] tracking-[0.3em] uppercase font-bold mb-5 flex items-center gap-2" data-id="element-1893">
              <span className="w-1 h-1 bg-[#D4AF37] rounded-full" data-id="element-1894" />
              Step 01 · Name Your Misl
            </p>
            <div className="space-y-5" data-id="element-1895">
              <div data-id="element-1896">
                <label className="block text-[#FDFBF7]/70 text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-1897">
                  Misl Name
                </label>
                <input type="text" value={name} onChange={e => setName(e.target.value)} placeholder="The Singh House" autoFocus className="w-full bg-[#131B2B] border border-[#D4AF37]/25 px-4 py-3.5 text-[#FDFBF7] text-base placeholder:text-[#FDFBF7]/25 focus:outline-none focus:border-[#D4AF37]/60 transition-colors" data-id="element-1898" />
                <p className="mt-2 text-[#FDFBF7]/35 text-[10px] leading-relaxed" data-id="element-1899">
                  Tip — strong Misl names are short, distinct, and feel like
                  yours.
                </p>
              </div>
              <div data-id="element-1900">
                <label className="block text-[#FDFBF7]/70 text-[10px] tracking-[0.3em] uppercase mb-2" data-id="element-1901">
                  City{' '}
                  <span className="text-[#FDFBF7]/30 normal-case tracking-normal" data-id="element-1902">
                    (optional)
                  </span>
                </label>
                <div className="relative" data-id="element-1903">
                  <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#D4AF37]/40" data-id="element-1904" />
                  <input type="text" value={region} onChange={e => setRegion(e.target.value)} placeholder="Toronto" className="w-full bg-[#131B2B] border border-[#D4AF37]/25 pl-11 pr-4 py-3.5 text-[#FDFBF7] text-base placeholder:text-[#FDFBF7]/25 focus:outline-none focus:border-[#D4AF37]/60 transition-colors" data-id="element-1905" />
                </div>
              </div>
              <button onClick={handleCreate} disabled={!name.trim()} className={`group w-full py-4 text-[10px] tracking-[0.3em] uppercase font-bold flex items-center justify-center gap-2.5 transition-all ${name.trim() ? 'bg-[#D4AF37] text-[#0B0F19] hover:bg-[#E5C158] shadow-[0_0_40px_rgba(212,175,55,0.35)]' : 'bg-[#D4AF37]/20 text-[#0B0F19]/40 cursor-not-allowed'}`} data-id="element-1906">
                <Crown className="w-4 h-4" data-id="element-1907" />
                Found My Misl
                <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" data-id="element-1908" />
              </button>
              <p className="text-[#FDFBF7]/40 text-[10px] text-center leading-relaxed" data-id="element-1909">
                Free · You can rename, add a city, or change your crest later
              </p>
            </div>
          </motion.div>
        </div> /* ============ STAGE 2: FORMING MISL ============ */ : <div className="space-y-6 relative" data-id="element-1910">
          {/* Just-created celebration overlay */}
          <AnimatePresence data-id="element-1911">
            {justCreated && <motion.div initial={{
          opacity: 0,
          y: -10
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -10
        }} className="absolute -top-2 left-0 right-0 z-20 flex justify-center pointer-events-none" data-id="element-1912">
                <div className="flex items-center gap-2 px-4 py-2.5 bg-[#D4AF37] text-[#0B0F19] text-[10px] tracking-[0.3em] uppercase font-bold shadow-[0_0_30px_rgba(212,175,55,0.5)]" data-id="element-1913">
                  <PartyPopper className="w-3.5 h-3.5" data-id="element-1914" />
                  Misl Founded — Now Bring Your People
                </div>
              </motion.div>}
          </AnimatePresence>

          {/* Status hero */}
          <motion.div initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.4
      }} className="border border-[#D4AF37]/30 bg-gradient-to-br from-[#1A1410] via-[#131B2B] to-[#0B0F19] p-6 relative overflow-hidden" data-id="element-1915">
            <CornerOrnaments data-id="element-1916" />
            <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-1917" />
            <div className="absolute -right-20 -top-20 w-56 h-56 bg-[#D4AF37]/10 blur-[100px] rounded-full pointer-events-none" data-id="element-1918" />
            <div className="relative z-10 flex items-start justify-between gap-4 mb-5 flex-wrap" data-id="element-1919">
              <div data-id="element-1920">
                <div className="flex items-center gap-2 mb-2" data-id="element-1921">
                  <span className={`w-2 h-2 rounded-full ${isActive ? 'bg-[#D4AF37]' : 'bg-[#FDFBF7]/50 animate-pulse'} shadow-[0_0_10px_currentColor]`} data-id="element-1922" />
                  <span className={`text-[9px] tracking-[0.3em] uppercase font-bold ${isActive ? 'text-[#D4AF37]' : 'text-[#FDFBF7]/70'}`} data-id="element-1923">
                    {isActive ? 'Active Misl' : 'Forming Misl'}
                  </span>
                </div>
                <p className="font-serif text-4xl text-[#FDFBF7] leading-tight" data-id="element-1924">
                  {memberCount}{' '}
                  <span className="text-[#FDFBF7]/30" data-id="element-1925">/ {target}</span>
                </p>
                <p className="text-[#D4AF37] text-xs tracking-[0.2em] uppercase mt-1 font-medium" data-id="element-1926">
                  {isActive ? '🔥 Your house is live' : `${target - memberCount} more to activate`}
                </p>
              </div>
              {region && <div className="flex items-center gap-1.5 text-[#FDFBF7]/40 text-[10px] tracking-[0.25em] uppercase" data-id="element-1927">
                  <MapPin className="w-3 h-3" data-id="element-1928" />
                  {region}
                </div>}
            </div>
            <div className="relative z-10 h-2 bg-[#D4AF37]/10 overflow-hidden" data-id="element-1929">
              <motion.div animate={{
            width: `${progress}%`
          }} transition={{
            duration: 0.6,
            ease: 'easeOut'
          }} className="absolute inset-y-0 left-0 bg-gradient-to-r from-[#D4AF37]/60 via-[#D4AF37] to-[#E5C158] shadow-[0_0_15px_rgba(212,175,55,0.4)]" data-id="element-1930" />
            </div>
          </motion.div>

          {/* Next-step nudge */}
          {!isActive && <div className="border border-[#D4AF37]/25 bg-[#D4AF37]/[0.04] px-4 py-3 flex items-center gap-3" data-id="element-1931">
              <Zap className="w-4 h-4 text-[#D4AF37] shrink-0" data-id="element-1932" />
              <p className="text-[#FDFBF7]/75 text-xs leading-relaxed" data-id="element-1933">
                <span className="text-[#D4AF37] font-bold" data-id="element-1934">Next step:</span>{' '}
                Send your invite link to {target - memberCount} friends — anyone
                you bring in can also invite more.
              </p>
            </div>}

          {/* Big invite block */}
          <div className="border border-[#D4AF37]/30 bg-gradient-to-br from-[#131B2B] to-[#0B0F19] p-6 relative" data-id="element-1935">
            <span className="absolute -top-px left-6 right-6 h-px bg-gradient-to-r from-transparent via-[#D4AF37] to-transparent" data-id="element-1936" />
            <div className="flex items-center gap-3 mb-4" data-id="element-1937">
              <UserPlus className="w-4 h-4 text-[#D4AF37]" data-id="element-1938" />
              <p className="font-serif text-base text-[#FDFBF7]" data-id="element-1939">
                Invite Your People
              </p>
            </div>
            <div className="flex items-center gap-2 border border-[#D4AF37]/20 bg-[#0B0F19] px-3 py-2.5 mb-3" data-id="element-1940">
              <span className="font-mono text-[#D4AF37]/80 text-xs truncate flex-1" data-id="element-1941">
                {inviteLink}
              </span>
              <button onClick={handleCopy} className="shrink-0 flex items-center gap-1.5 px-2.5 py-1 border border-[#D4AF37]/30 text-[#D4AF37] text-[9px] tracking-[0.2em] uppercase hover:bg-[#D4AF37]/10 transition-colors" data-id="element-1942">
                {copied ? <>
                    <Check className="w-2.5 h-2.5" data-id="element-1943" />
                    Copied
                  </> : <>
                    <Copy className="w-2.5 h-2.5" data-id="element-1944" />
                    Copy
                  </>}
              </button>
            </div>
            <button onClick={handleWhatsApp} className="w-full flex items-center justify-center gap-2.5 px-6 py-3.5 bg-[#25D366] text-white hover:bg-[#128C7E] transition-colors text-[10px] tracking-[0.25em] uppercase font-bold" data-id="element-1945">
              <MessageCircle className="w-4 h-4" data-id="element-1946" />
              Share on WhatsApp
            </button>
            <p className="text-[#FDFBF7]/40 text-[10px] text-center mt-3 leading-relaxed" data-id="element-1947">
              Every member can invite. Build your house together.
            </p>
          </div>

          {/* Member list */}
          <div className="border border-[#D4AF37]/20 bg-[#0B0F19]" data-id="element-1948">
            <div className="flex items-center justify-between px-5 py-3 border-b border-[#D4AF37]/15" data-id="element-1949">
              <p className="text-[#D4AF37]/70 text-[10px] tracking-[0.3em] uppercase" data-id="element-1950">
                Members
              </p>
              <span className="font-mono text-[#D4AF37] text-xs" data-id="element-1951">
                {memberCount} / {target}
              </span>
            </div>
            <div className="divide-y divide-[#D4AF37]/10" data-id="element-1952">
              {members.map(m => <motion.div key={m.id} initial={{
            opacity: 0,
            y: -8
          }} animate={{
            opacity: 1,
            y: 0
          }} className="flex items-center gap-3 px-5 py-3 hover:bg-[#131B2B]/40 transition-colors" data-id="element-1953">
                  <div className={`w-9 h-9 shrink-0 rounded-full border ${m.role === 'founder' ? 'border-[#D4AF37] bg-[#D4AF37]/15' : 'border-[#D4AF37]/30 bg-[#131B2B]'} flex items-center justify-center`} data-id="element-1954">
                    {m.role === 'founder' ? <Crown className="w-4 h-4 text-[#D4AF37]" data-id="element-1955" /> : <User className="w-4 h-4 text-[#FDFBF7]/50" data-id="element-1956" />}
                  </div>
                  <div className="flex-1 min-w-0" data-id="element-1957">
                    <p className="font-serif text-sm text-[#FDFBF7] leading-tight truncate" data-id="element-1958">
                      {m.name}
                    </p>
                    <p className="font-mono text-[#FDFBF7]/40 text-[10px] truncate" data-id="element-1959">
                      {m.handle} · {m.joinedAt}
                    </p>
                  </div>
                  {m.role === 'founder' && <span className="shrink-0 text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase border border-[#D4AF37]/40 bg-[#D4AF37]/10 px-2 py-0.5" data-id="element-1960">
                      Founder
                    </span>}
                  <button onClick={handleWhatsApp} className="shrink-0 text-[#D4AF37]/60 hover:text-[#D4AF37] text-[9px] tracking-[0.25em] uppercase border border-[#D4AF37]/20 hover:border-[#D4AF37]/50 px-2 py-1 transition-colors flex items-center gap-1" data-id="element-1961">
                    <Share2 className="w-2.5 h-2.5" data-id="element-1962" />
                    Invite
                  </button>
                </motion.div>)}
              {Array.from({
            length: Math.max(0, target - memberCount)
          }).map((_, i) => <div key={`empty-${i}`} className="flex items-center gap-3 px-5 py-3 opacity-60" data-id="element-1963">
                  <div className="w-9 h-9 shrink-0 rounded-full border border-dashed border-[#D4AF37]/20 bg-[#0B0F19] flex items-center justify-center" data-id="element-1964">
                    <Plus className="w-3 h-3 text-[#D4AF37]/30" data-id="element-1965" />
                  </div>
                  <p className="text-[#FDFBF7]/30 text-xs italic flex-1" data-id="element-1966">
                    Empty seat — invite someone
                  </p>
                  {i === 0 && memberCount < target && <button onClick={handleSimulateJoin} disabled={simulating} className="shrink-0 text-[#FDFBF7]/30 hover:text-[#D4AF37]/70 text-[9px] tracking-[0.25em] uppercase transition-colors" data-id="element-1967">
                      {simulating ? '...' : 'Simulate join'}
                    </button>}
                </div>)}
            </div>
          </div>

          {/* Active reward unlock teaser */}
          {!isActive && <div className="border border-[#D4AF37]/15 bg-[#0B0F19] p-4 flex items-center gap-4" data-id="element-1968">
              <Trophy className="w-5 h-5 text-[#D4AF37]/70 shrink-0" data-id="element-1969" />
              <div className="flex-1" data-id="element-1970">
                <p className="text-[#FDFBF7] text-sm leading-tight" data-id="element-1971">
                  Unlock at{' '}
                  <span className="text-[#D4AF37] font-bold" data-id="element-1972">5 members</span>
                </p>
                <p className="text-[#FDFBF7]/45 text-[11px] leading-relaxed mt-0.5" data-id="element-1973">
                  Misl Leaderboard · Group XP · Founder Crown · Court rank
                </p>
              </div>
              <span className="font-mono text-[#D4AF37] text-sm" data-id="element-1974">
                {target - memberCount}{' '}
                <span className="text-[#FDFBF7]/40 text-[10px] uppercase tracking-widest" data-id="element-1975">
                  to go
                </span>
              </span>
            </div>}
        </div>}
    </div>;
};